import { Box, Collapse, FormControlLabel, Radio, Stack, Typography, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useTranslation } from 'react-i18next';

//Components
import CheckoutDone from 'src/asset/icons/checkout-done-step.svg';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import IconFeather from 'src/components/common/Icon';
import { CheckoutStep } from 'src/features/Checkout/config';
import { getTimeFromPlanType, renderPriceOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';

// Utilities
import dayjs from 'dayjs';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { ICheckoutForm } from 'src/utils/typings/dto/payment';
import { IPlanDetail } from 'src/utils/typings/response';

const CheckOrder = ({
  open,
  listPrices,
  isStepDone,
  planDetail,
  handleChangeStep,
}: {
  open: boolean;
  isStepDone: boolean;
  listPrices: Record<ExpectedAny, number>;
  planDetail: IPlanDetail;
  handleChangeStep: (step: CheckoutStep) => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const formik = useFormikContext<ICheckoutForm>();

  const planTypeShortest = Object.keys(listPrices)[0];

  const calculateSavedMoney = (planType: string) => {
    const minTime = getTimeFromPlanType(planTypeShortest);
    const currentTime = getTimeFromPlanType(planType);
    return listPrices[planTypeShortest] * (currentTime / minTime) - listPrices[planType];
  };

  return (
    <Stack direction="column">
      <Stack direction="row" spacing={1.5} alignItems="center">
        {isStepDone ? (
          <Box borderRadius={999} bgcolor={theme.palette.primary.main} width={40} height={40} position="relative">
            <Box
              component={'img'}
              src={CheckoutDone}
              position="absolute"
              left="50%"
              top="50%"
              sx={{
                transform: 'translate(-50%, -50%)',
              }}
            />
          </Box>
        ) : (
          <Box borderRadius={999} border={`1px solid #000`} py={1} px={2}>
            <Typography variant="body1" component="span">
              1
            </Typography>
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flex={1}
          mb={3}
          sx={{ cursor: 'pointer' }}
          onClick={() => handleChangeStep(CheckoutStep.CheckOrder)}
        >
          <Typography variant="h6" color={isStepDone ? 'primary.main' : 'text.primary'}>
            {t('checkout.check_order.title')}
          </Typography>
          <IconFeather icon={open ? <ChevronUp /> : <ChevronDown />} />
        </Stack>
      </Stack>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack direction="column" spacing={3} pl={7} mt={3}>
          <Typography variant="body1">
            {t('checkout.check_order.selected_lan')}{' '}
            <Typography variant="h6" component="span">
              {renderTextOrDefault(planDetail.plan_name)}
            </Typography>
          </Typography>
          <Stack spacing={1.5} direction="row">
            {Object.keys(listPrices).map((plan) => {
              const isSelected = formik.values.plan_type === plan;
              const moneySaved = calculateSavedMoney(plan);
              return (
                <Box
                  key={plan}
                  border={`1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`}
                  borderRadius="12px"
                  p={2.5}
                  position="relative"
                  sx={{ cursor: 'pointer' }}
                  pb={4}
                  width="25%"
                  onClick={() => formik.setFieldValue('plan_type', plan)}
                >
                  <FormControlLabel
                    value="male"
                    control={<Radio color="primary" checked={isSelected} />}
                    label={<Typography variant="h6">{t(`pricing.plan_type.${plan}`)}</Typography>}
                  />
                  <Box mt={1.5} />
                  <Typography variant="h5" align="center">
                    ${renderPriceOrDefault(get(planDetail, `price_${plan}`, 0))}
                  </Typography>
                  <Box mt={0.5} />
                  <Typography variant="body2" align="center" color="text.secondary">
                    USD/{t(`pricing.plan_time.${plan}`)}
                  </Typography>
                  <Box mt={1.5} />
                  <Typography variant="caption" align="center" color="text.secondary" component="p">
                    {t('checkout.check_order.plan_renew')
                      .replace(
                        '{{price}}',
                        `$${renderPriceOrDefault(get(planDetail, `price_${plan}`))}/ ${t(`pricing.plan_time.${plan}`)}`,
                      )
                      .replace(
                        '{{date}}',
                        dayjs().add(getTimeFromPlanType(plan), 'month').format(FORMAT_DATE_TIME.FULL_ONLY_DATE),
                      )}
                  </Typography>
                  {plan !== planTypeShortest && moneySaved > 0 && (
                    <Box
                      bgcolor={isSelected ? theme.palette.primary.main : theme.palette.textTertiary.main}
                      py={1}
                      px={2}
                      borderRadius={24}
                      position="absolute"
                      bottom={theme.spacing(-2)}
                      left="50%"
                      width="max-content"
                      sx={{
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <Typography variant="subtitle2" color="white" align="center">
                        {t('global.save')} ${renderPriceOrDefault(moneySaved)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" pt={2}>
            <ButtonWithLoading
              variant="contained"
              size="large"
              disabled={!formik.values.plan_type}
              onClick={() => handleChangeStep(CheckoutStep.BillingInformation)}
            >
              {t('global.next')}
            </ButtonWithLoading>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default CheckOrder;
